import { AppStoreBadge } from '@/components/app-store-badge';
import { BabysitterMode } from '@/components/babysitter-mode';
import { ScreenshotShowcase } from '@/components/screenshot-showcase';

export default function HomePage() {
  return (
    <>
      <Hero />
      <ScreenshotShowcase />
      <BabysitterMode />
    </>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <VineBackdrop />

      <div className="container relative grid gap-14 pb-8 pt-20 sm:pt-28 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-center lg:pb-16">
        <div>
          <p className="kicker animate-rise">Free &middot; ad-free &middot; iPhone</p>

          <h1
            className="mt-6 animate-rise text-[clamp(3.6rem,13vw,8.5rem)] font-semibold leading-[0.86] tracking-[-0.03em]"
            style={{ animationDelay: '80ms' }}
          >
            Bramble
          </h1>

          <p
            className="mt-7 max-w-xl animate-rise text-[clamp(1.1rem,2vw,1.35rem)] leading-snug text-foreground/85"
            style={{ animationDelay: '160ms' }}
          >
            A privacy-first baby tracker for parents that syncs end-to-end encrypted between your
            phones. No accounts, no ads, no subscriptions, and no tracking. Ever.
          </p>

          <div
            className="mt-10 flex animate-rise flex-wrap items-center gap-5"
            style={{ animationDelay: '240ms' }}
          >
            <AppStoreBadge />
            <a
              href="#screens"
              className="text-sm text-muted-foreground underline decoration-border decoration-2 underline-offset-[6px] transition-colors hover:text-foreground hover:decoration-berry"
            >
              See the app
            </a>
          </div>
        </div>

        <ClaimsPanel />
      </div>
    </section>
  );
}

/**
 * Three short claims about what the app does, read as a sequence: log it, shape
 * it to how you track, then see what it adds up to. The privacy argument these
 * replaced now lives in the tagline above, so it isn't repeated here.
 *
 * Every claim has to stay literally true of the shipped app — no roadmap. The
 * "Highly configurable" title is deliberately broad enough to cover theming
 * later, but its body only names settings that exist today.
 */
const CLAIMS = [
  {
    n: '01',
    title: 'One-tap logging',
    body: 'Feeds, diapers, sleep, and potty trips log from the home screen, nursing timers included. Works offline.',
  },
  {
    n: '02',
    title: 'Highly configurable',
    body: 'Hide what you do not track, per child. Pin the charts that matter and switch between metric and imperial.',
  },
  {
    n: '03',
    title: 'Trends that add up',
    body: 'Growth, feeding, sleep, diapers, and potty training charted over time, with week, month, and all-time averages.',
  },
];

function ClaimsPanel() {
  return (
    <ul className="relative animate-rise space-y-0" style={{ animationDelay: '400ms' }}>
      {CLAIMS.map((claim, i) => (
        <li
          key={claim.n}
          className="group relative border-t border-border py-6 last:border-b sm:py-7"
          style={{ animationDelay: `${440 + i * 90}ms` }}
        >
          <div className="flex gap-5">
            <span className="font-mono text-xs leading-7 text-berry">{claim.n}</span>
            <div>
              <h2 className="font-display text-[1.3rem] font-semibold leading-snug">
                {claim.title}
              </h2>
              <p className="mt-1.5 text-[0.98rem] leading-relaxed text-muted-foreground">
                {claim.body}
              </p>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Decorative bramble stem that draws itself in behind the wordmark. */
function VineBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <svg
        viewBox="0 0 1200 600"
        preserveAspectRatio="xMidYMid slice"
        className="absolute -right-24 -top-24 h-[130%] w-[110%] text-berry/25 sm:-right-10"
      >
        <path
          d="M-40 520 C 180 470, 260 330, 430 300 S 700 330, 840 220 S 1080 90, 1260 120"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeDasharray="1400"
          className="animate-draw-vine"
        />
        <path
          d="M-40 600 C 220 560, 340 430, 520 420 S 820 470, 980 350 S 1180 250, 1260 260"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="1400"
          className="animate-draw-vine"
          style={{ animationDelay: '500ms' }}
          opacity="0.6"
        />
        <g className="animate-drift">
          <circle cx="430" cy="300" r="7" className="fill-berry/40" />
          <circle cx="448" cy="288" r="4.5" className="fill-berry/30" />
          <circle cx="840" cy="220" r="9" className="fill-berry/30" />
          <circle cx="858" cy="234" r="5" className="fill-berry/20" />
          <circle cx="520" cy="420" r="6" className="fill-leaf/30" />
        </g>
      </svg>

      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-background" />
    </div>
  );
}
